import { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Box, Layers, AlertTriangle } from 'lucide-react';
import { axios } from '../App';
import { toast } from 'sonner';

export default function ProtectionView3D() {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const animationRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [sensors, setSensors] = useState([]);
  const [detections, setDetections] = useState([]);
  const [hasError, setHasError] = useState(false);

  // Load data
  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [sensorsRes, detectionsRes] = await Promise.all([
        axios.get('/sensors'),
        axios.get('/drones')
      ]);
      setSensors(sensorsRes.data || []);
      const activeDetections = (detectionsRes.data || []).filter(d => d.status === 'active');
      setDetections(activeDetections);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initialize Three.js scene
  useEffect(() => {
    if (loading || !containerRef.current) return;

    try {
      // Scene
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x020617);
      scene.fog = new THREE.FogExp2(0x020617, 0.015);
      sceneRef.current = scene;

      // Camera
      const camera = new THREE.PerspectiveCamera(
        55,
        containerRef.current.clientWidth / containerRef.current.clientHeight,
        0.1,
        1000
      );
      camera.position.set(25, 18, 25);
      camera.lookAt(0, 0, 0);
      cameraRef.current = camera;

      // Renderer
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
      renderer.setPixelRatio(window.devicePixelRatio);
      containerRef.current.appendChild(renderer.domElement);
      rendererRef.current = renderer;

      // Controls
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.minDistance = 10;
      controls.maxDistance = 60;
      controlsRef.current = controls;

      // Lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(10, 20, 10);
      scene.add(directionalLight);

      const pointLight = new THREE.PointLight(0x0ea5e9, 0.5, 100);
      pointLight.position.set(0, 10, 0);
      scene.add(pointLight);

      // Ocean surface
      const oceanGeometry = new THREE.PlaneGeometry(100, 100);
      const oceanMaterial = new THREE.MeshStandardMaterial({
        color: 0x0c4a6e,
        transparent: true,
        opacity: 0.4,
        side: THREE.DoubleSide
      });
      const ocean = new THREE.Mesh(oceanGeometry, oceanMaterial);
      ocean.rotation.x = -Math.PI / 2;
      ocean.position.y = -1;
      scene.add(ocean);

      // Naval vessel
      createNavalVessel(scene);

      // Protection spheres (concentric layers - full spherical coverage)
      // Scale: 1km = 3 units for better visualization
      createProtectionSphere(scene, 15, 0x0ea5e9, 0.12);  // Long Range: 5km
      createProtectionSphere(scene, 6, 0x06b6d4, 0.15);   // Medium Range: 2km
      createProtectionSphere(scene, 3, 0x14b8a6, 0.18);   // Close-In: 1km

      // Add threat detections
      detections.forEach((detection) => {
        createThreatMarker(scene, detection);
      });

      // Animation loop
      const animate = () => {
        animationRef.current = requestAnimationFrame(animate);
        
        // Rotate protection spheres
        scene.children.forEach((child) => {
          if (child.userData.isProtectionSphere) {
            child.rotation.y += 0.001;
          }
          if (child.userData.isThreat) {
            const scale = 1 + Math.sin(Date.now() * 0.004) * 0.15;
            child.scale.setScalar(scale);
          }
        });

        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      // Handle resize
      const handleResize = () => {
        if (!containerRef.current) return;
        camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
      };
      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        if (rendererRef.current && containerRef.current) {
          containerRef.current.removeChild(rendererRef.current.domElement);
        }
        rendererRef.current?.dispose();
      };
    } catch (error) {
      console.error('Three.js initialization error:', error);
      setHasError(true);
    }
  }, [loading, sensors, detections]);

  // Create naval vessel
  const createNavalVessel = (scene) => {
    const group = new THREE.Group();
    const scale = 0.4; // Scale down the ship

    // Hull - Yellow for visibility
    const hullGeom = new THREE.BoxGeometry(2 * scale, 1 * scale, 6 * scale);
    const hullMat = new THREE.MeshStandardMaterial({ color: 0xfbbf24, metalness: 0.5, roughness: 0.3 });
    const hull = new THREE.Mesh(hullGeom, hullMat);
    hull.position.y = -0.5 * scale;
    group.add(hull);

    // Deck - Slightly darker yellow
    const deckGeom = new THREE.BoxGeometry(1.8 * scale, 0.2 * scale, 5.5 * scale);
    const deckMat = new THREE.MeshStandardMaterial({ color: 0xf59e0b, metalness: 0.4, roughness: 0.4 });
    const deck = new THREE.Mesh(deckGeom, deckMat);
    deck.position.y = 0.1 * scale;
    group.add(deck);

    // Bridge - Orange-yellow
    const bridgeGeom = new THREE.BoxGeometry(1.2 * scale, 1.2 * scale, 1.5 * scale);
    const bridgeMat = new THREE.MeshStandardMaterial({ color: 0xeab308, metalness: 0.5, roughness: 0.3 });
    const bridge = new THREE.Mesh(bridgeGeom, bridgeMat);
    bridge.position.set(0, 0.8 * scale, -1 * scale);
    group.add(bridge);

    // Radar mast - Dark for contrast
    const mastGeom = new THREE.CylinderGeometry(0.05 * scale, 0.05 * scale, 1.2 * scale, 8);
    const mastMat = new THREE.MeshStandardMaterial({ color: 0x27272a });
    const mast = new THREE.Mesh(mastGeom, mastMat);
    mast.position.set(0, 1.8 * scale, -1 * scale);
    group.add(mast);

    // Radar dome
    const domeGeom = new THREE.SphereGeometry(0.25 * scale, 16, 16);
    const domeMat = new THREE.MeshStandardMaterial({ 
      color: 0x0ea5e9, 
      emissive: 0x0ea5e9, 
      emissiveIntensity: 0.3 
    });
    const dome = new THREE.Mesh(domeGeom, domeMat);
    dome.position.set(0, 2.5 * scale, -1 * scale);
    group.add(dome);

    scene.add(group);
  };

  // Create protection sphere
  const createProtectionSphere = (scene, radius, color, opacity) => {
    const geometry = new THREE.SphereGeometry(radius, 32, 32);
    const material = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: opacity,
      wireframe: true,
      side: THREE.DoubleSide
    });
    const sphere = new THREE.Mesh(geometry, material);
    sphere.userData.isProtectionSphere = true;
    scene.add(sphere);
  };

  // Create sensor marker
  const createSensorMarker = (scene, sensor) => {
    const colors = {
      air: 0x0ea5e9,
      surface: 0x10b981,
      underwater: 0x3b82f6
    };
    
    const yPosition = sensor.domain === 'air' ? 3 : sensor.domain === 'surface' ? 0.5 : -3;
    const color = colors[sensor.domain] || 0x0ea5e9;
    
    const geometry = new THREE.SphereGeometry(0.3, 16, 16);
    const material = new THREE.MeshStandardMaterial({
      color: color,
      emissive: color,
      emissiveIntensity: 0.3
    });
    const marker = new THREE.Mesh(geometry, material);
    marker.position.set(
      (sensor.lng - 83.2185) * 50,
      yPosition,
      (sensor.lat - 17.6868) * 50
    );
    scene.add(marker);
  };

  // Create threat marker
  const createThreatMarker = (scene, detection) => {
    const colors = {
      high: 0xef4444,
      medium: 0xf59e0b,
      low: 0x22c55e,
      unknown: 0x6366f1
    };
    
    const color = colors[detection.threat_level?.toLowerCase()] || colors.unknown;
    const yPosition = detection.domain === 'air' 
      ? Math.min((detection.altitude || 100) / 20, 12) 
      : detection.domain === 'surface' ? 0.5 : -4;

    // Threat geometry based on domain
    let geometry;
    const domain = detection.domain?.toLowerCase() || '';
    
    if (domain === 'air') {
      // Diamond shape for all air threats
      geometry = new THREE.OctahedronGeometry(0.5, 0);
    } else if (domain === 'surface') {
      // Box shape for surface threats
      geometry = new THREE.BoxGeometry(0.6, 0.3, 0.5);
    } else if (domain === 'underwater') {
      // Capsule/elongated shape for underwater threats
      geometry = new THREE.SphereGeometry(0.4, 16, 16);
    } else {
      geometry = new THREE.SphereGeometry(0.4, 16, 16);
    }

    const material = new THREE.MeshStandardMaterial({
      color: color,
      emissive: color,
      emissiveIntensity: 0.5
    });

    const marker = new THREE.Mesh(geometry, material);
    marker.position.set(
      (detection.lng - 83.2185) * 50,
      yPosition,
      (detection.lat - 17.6868) * 50
    );
    marker.userData.isThreat = true;
    scene.add(marker);

    // Add ring indicator
    const ringGeom = new THREE.RingGeometry(0.6, 0.7, 32);
    const ringMat = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide
    });
    const ring = new THREE.Mesh(ringGeom, ringMat);
    ring.position.copy(marker.position);
    ring.rotation.x = Math.PI / 2;
    scene.add(ring);
  };

  const stats = {
    air: detections.filter(d => d.domain === 'air').length,
    surface: detections.filter(d => d.domain === 'surface').length,
    underwater: detections.filter(d => d.domain === 'underwater').length,
    threats: detections.length
  };

  if (hasError) {
    return (
      <div className="h-full flex flex-col bg-zinc-950" data-testid="3d-view-page">
        <div className="bg-zinc-900/50 border-b border-zinc-800 p-4">
          <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
            <Box size={24} className="text-sky-500" />
            3D Protection Layer
          </h1>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-8">
            <AlertTriangle size={48} className="text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-zinc-100 mb-2">3D View Error</h3>
            <p className="text-sm text-zinc-400">WebGL rendering encountered an issue. Please try refreshing.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-zinc-950" data-testid="3d-view-page">
      {/* Header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
              <Box size={24} className="text-sky-500" />
              3D Protection Layer
            </h1>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
              Multi-Domain Defense Coverage Visualization
            </p>
          </div>
          <div className="flex items-center gap-4 text-xs font-mono">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-sky-500"></div>
              <span className="text-zinc-400">Air: {stats.air}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
              <span className="text-zinc-400">Surface: {stats.surface}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-zinc-400">Underwater: {stats.underwater}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
              <span className="text-red-400">Threats: {stats.threats}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Canvas Container */}
      <div className="flex-1 relative">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-sky-500 font-mono text-sm animate-pulse">
              INITIALIZING 3D PROTECTION VIEW...
            </div>
          </div>
        ) : (
          <div ref={containerRef} className="w-full h-full" />
        )}

        {/* UI Overlays */}
        {!loading && (
          <>
            {/* Controls Panel */}
            <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur-md border border-zinc-800 p-4 rounded-sm max-w-xs">
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3 text-sky-500 flex items-center gap-2">
                <Layers size={16} />
                Protection Layers
              </h3>
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-sky-500/50 border border-sky-500"></div>
                    <span className="text-zinc-400">Long Range:</span>
                  </div>
                  <span className="text-sky-400 font-mono">5km</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-cyan-400/50 border border-cyan-400"></div>
                    <span className="text-zinc-400">Medium Range:</span>
                  </div>
                  <span className="text-cyan-400 font-mono">2km</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-teal-400/50 border border-teal-400"></div>
                    <span className="text-zinc-400">Close-In:</span>
                  </div>
                  <span className="text-teal-400 font-mono">1km</span>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-zinc-700 text-xs text-zinc-500 space-y-1">
                <p><span className="text-zinc-400">Rotate:</span> Left click + drag</p>
                <p><span className="text-zinc-400">Zoom:</span> Scroll wheel</p>
                <p><span className="text-zinc-400">Pan:</span> Right click + drag</p>
              </div>
            </div>

            {/* Legend Panel */}
            <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-md border border-zinc-800 p-4 rounded-sm">
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3">Threat Legend</h3>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500/30 border border-red-500 rounded"></div>
                  <span className="text-zinc-300">High Threat</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-500/30 border border-orange-500 rounded"></div>
                  <span className="text-zinc-300">Medium Threat</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500/30 border border-green-500 rounded"></div>
                  <span className="text-zinc-300">Low Threat</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
